import { useState } from 'react';
import { Button, Divider, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { PATH_PAGE } from '../routes/paths';

import { MUTATION_ACTIVATE_LICENSE } from '../graphql/license';

export default function LicenseForm({ onSuccess }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // ---------------------
  const [onActivateMutation] = useMutation(MUTATION_ACTIVATE_LICENSE);

  // ---------------------
  const [licenseCode, setLicenseCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------------------
  const onVerify = async () => {
    if (!licenseCode) {
      return enqueueSnackbar(t('Enter your license key'), { variant: 'warning' });
    }

    setIsSubmitting(true);
    try {
      const result = await onActivateMutation({ variables: { licenseCode } });

      const activate = result?.data?.license?.activate;

      if (activate?.Activated) {
        setLicenseCode('');

        if (activate?.SessionId) {
          localStorage.setItem('SESSION_ID', activate?.SessionId);
        }

        onSuccess();
      } else if (activate?.AvailableToTransfer && licenseCode) {
        navigate(PATH_PAGE.availableToTransfer(licenseCode));
      } else {
        enqueueSnackbar(t(activate.Message), { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err?.graphQLErrors?.[0]?.message || t('An unexpected error has occurred'), {
        variant: 'error',
      });
    }

    setIsSubmitting(false);
    return true;
  };

  // ---------------------
  return (
    <>
      <Typography sx={{ mb: 2 }} variant="subtitle1">
        {t('Enter your license key or purchase code.')}
      </Typography>

      <TextField
        value={licenseCode}
        onChange={(e) => setLicenseCode(e.target.value)}
        label={t('License Key')}
        fullWidth
      />

      <LoadingButton
        variant="contained"
        size="large"
        onClick={onVerify}
        fullWidth
        sx={{ mt: 3 }}
        loading={isSubmitting}
      >
        {t('Verify')}
      </LoadingButton>

      <Divider sx={{ my: 3 }}>{t('OR')}</Divider>

      <Button
        variant="outlined"
        href="https://dalga.dev/product/awesome-traffic-bot/"
        target="_blank"
        fullWidth
        size="large"
      >
        {t('Buy a license key')}
      </Button>
    </>
  );
}
